import {
  trackSpeakerAiVoiceAdded,
  trackSpeakerAiVoiceLanguageDefaultSet,
  trackSpeakerAiVoiceRemoved,
} from '@src/analytics/EventTracker';
import { useDubbingStore, useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { SpeakerAiVoiceMapping } from '@src/repositories/SpeakerRepository';

const logger = LogManager.getLogger('aiVoices:useSpeakerAiVoices');

// Loads and mutates one speaker's AI Voice association (DE4/DI19): the ordered
// `ai_voices` (top = default) plus the optional per-language pin map. Every
// mutation persists immediately (optimistic in-memory update →
// SpeakerRepository.updateSpeakerAiVoices → rollback + error toast on failure),
// mirroring the workspace-activation hook `useVoiceActivation`. The Manage AI
// Voices modal (both speaker menus) drives this; voice records themselves are
// resolved from the workspace pool by the caller.
export function useSpeakerAiVoices(speakerId: string | null) {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const dubbingStore = useDubbingStore();
  const [aiVoiceIds, setAiVoiceIds] = useState<string[]>([]);
  const [languagePrefs, setLanguagePrefs] = useState<Record<string, string>>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // The live mapping, for callbacks that outlive the render that created them:
  // a removal toast's Undo can fire after further edits, and `persist`'s
  // rollback must match what actually persisted, not a captured snapshot.
  const currentRef = useRef<SpeakerAiVoiceMapping>({
    aiVoiceIds: [],
    languagePrefs: {},
  });
  currentRef.current = { aiVoiceIds, languagePrefs };

  // Re-sync local state with the cached mapping (cache-first). Called by a
  // surface editing through a separate hook instance (e.g. the pop-over info
  // tab after its Manage modal closes) — since edits write through the cache,
  // this reflects them without a refetch. Falls back to a fetch if uncached.
  const reload = useCallback(async (): Promise<void> => {
    if (!speakerId) return;
    const cached = dubbingStore.getSpeakerMapping(speakerId);
    if (cached) {
      setAiVoiceIds(cached.aiVoiceIds);
      setLanguagePrefs(cached.languagePrefs);
      return;
    }
    setLoading(true);
    const mapping =
      await datoClientStore.speakerRepository.getSpeakerAiVoices(speakerId);
    dubbingStore.setSpeakerMapping(speakerId, mapping);
    setAiVoiceIds(mapping.aiVoiceIds);
    setLanguagePrefs(mapping.languagePrefs);
    setLoading(false);
  }, [speakerId, datoClientStore, dubbingStore]);

  useEffect(() => {
    if (!speakerId) return undefined;
    // Cache-first: a speaker's mapping is fetched once and kept in the
    // DubbingStore, so re-opening the "AI Voices" tab is instant (no reload).
    const cached = dubbingStore.getSpeakerMapping(speakerId);
    if (cached) {
      setAiVoiceIds(cached.aiVoiceIds);
      setLanguagePrefs(cached.languagePrefs);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    void datoClientStore.speakerRepository
      .getSpeakerAiVoices(speakerId)
      .then(mapping => {
        if (cancelled) return;
        dubbingStore.setSpeakerMapping(speakerId, mapping);
        setAiVoiceIds(mapping.aiVoiceIds);
        setLanguagePrefs(mapping.languagePrefs);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [speakerId, datoClientStore, dubbingStore]);

  // Optimistically apply the next mapping, persist, and roll back on failure.
  const persist = useCallback(
    async (next: SpeakerAiVoiceMapping): Promise<boolean> => {
      if (!speakerId) return false;
      const prev = currentRef.current;
      setAiVoiceIds(next.aiVoiceIds);
      setLanguagePrefs(next.languagePrefs);
      // Write through the shared cache so other surfaces (the pop-over info tab
      // and the Manage modal share it) see the edit without a refetch.
      dubbingStore.setSpeakerMapping(speakerId, next);
      setBusy(true);
      const ok = await datoClientStore.speakerRepository.updateSpeakerAiVoices(
        speakerId,
        next,
      );
      setBusy(false);
      if (!ok) {
        setAiVoiceIds(prev.aiVoiceIds);
        setLanguagePrefs(prev.languagePrefs);
        dubbingStore.setSpeakerMapping(speakerId, prev);
        logger.error('Failed to persist speaker AI voices', { speakerId });
        videoCreator.showToast({
          state: 'error',
          message:
            "Could not update the speaker's AI Voices. Please try again.",
        });
      }
      return ok;
    },
    [speakerId, datoClientStore, dubbingStore, videoCreator],
  );

  const addVoice = useCallback(
    (voiceId: string) => {
      if (!speakerId || aiVoiceIds.includes(voiceId)) return;
      const nextIds = [...aiVoiceIds, voiceId];
      void persist({ aiVoiceIds: nextIds, languagePrefs }).then(ok => {
        if (ok)
          trackSpeakerAiVoiceAdded({
            speaker_id: speakerId,
            ai_voice_id: voiceId,
            voice_count: nextIds.length,
          });
      });
    },
    [speakerId, aiVoiceIds, languagePrefs, persist],
  );

  const removeVoice = useCallback(
    (voiceId: string) => {
      if (!speakerId) return;
      const at = aiVoiceIds.indexOf(voiceId);
      if (at === -1) return;
      const nextIds = aiVoiceIds.filter(id => id !== voiceId);
      // Drop any per-language pin that referenced the removed voice so a pin
      // never points at a voice the speaker no longer has.
      const entries = Object.entries(languagePrefs);
      const nextPrefs = Object.fromEntries(
        entries.filter(([, id]) => id !== voiceId),
      );
      const droppedPrefs = Object.fromEntries(
        entries.filter(([, id]) => id === voiceId),
      );
      void persist({ aiVoiceIds: nextIds, languagePrefs: nextPrefs }).then(
        ok => {
          if (!ok) return;
          trackSpeakerAiVoiceRemoved({
            speaker_id: speakerId,
            ai_voice_id: voiceId,
            voice_count: nextIds.length,
          });
          // Removal is one click with no confirmation, so the confirmation
          // toast carries the way back: Undo restores the voice at its old
          // position along with any language pins that pointed at it.
          const name = dubbingStore.voiceById(voiceId)?.name ?? 'Voice';
          videoCreator.showToast({
            state: 'success',
            message: `${name} removed from this speaker`,
            action: {
              label: 'Undo',
              onClick: () => {
                // Toasts stack, so several Undos can be live at once and a
                // reorder leaves no toast at all: undo *this* removal against
                // the current mapping rather than restoring a snapshot, which
                // would revert whatever came after it.
                const live = currentRef.current;
                if (live.aiVoiceIds.includes(voiceId)) return; // re-added by hand
                const restoredIds = [...live.aiVoiceIds];
                restoredIds.splice(at, 0, voiceId);
                void persist({
                  aiVoiceIds: restoredIds,
                  languagePrefs: { ...live.languagePrefs, ...droppedPrefs },
                });
              },
            },
          });
        },
      );
    },
    [speakerId, aiVoiceIds, languagePrefs, persist, dubbingStore, videoCreator],
  );

  // Move a voice to sit immediately before `beforeVoiceId`, or to the end when
  // that is null. Anchored by id rather than index: callers render only the
  // voices the catalog still resolves, so a position in their list is not a
  // position in `aiVoiceIds`. The head is the language-agnostic default, so a
  // move can re-pick that default.
  const moveVoice = useCallback(
    (voiceId: string, beforeVoiceId: string | null) => {
      if (voiceId === beforeVoiceId || !aiVoiceIds.includes(voiceId)) return;
      const nextIds = aiVoiceIds.filter(id => id !== voiceId);
      const at = beforeVoiceId ? nextIds.indexOf(beforeVoiceId) : -1;
      if (at === -1) nextIds.push(voiceId);
      else nextIds.splice(at, 0, voiceId);
      if (nextIds.every((id, i) => id === aiVoiceIds[i])) return;
      void persist({ aiVoiceIds: nextIds, languagePrefs });
    },
    [aiVoiceIds, languagePrefs, persist],
  );

  // Pin (or, with null, clear) the default voice for one language.
  const setLanguageDefault = useCallback(
    (languageId: string, voiceId: string | null) => {
      if (!speakerId) return;
      const nextPrefs = { ...languagePrefs };
      if (voiceId) nextPrefs[languageId] = voiceId;
      else delete nextPrefs[languageId];
      void persist({ aiVoiceIds, languagePrefs: nextPrefs }).then(ok => {
        if (ok)
          trackSpeakerAiVoiceLanguageDefaultSet({
            speaker_id: speakerId,
            language_id: languageId,
            ai_voice_id: voiceId,
          });
      });
    },
    [speakerId, aiVoiceIds, languagePrefs, persist],
  );

  return {
    aiVoiceIds,
    languagePrefs,
    loading,
    busy,
    reload,
    addVoice,
    removeVoice,
    moveVoice,
    setLanguageDefault,
  };
}
